import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { api } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  DocumentTextIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  EyeIcon,
  ClockIcon,
  CalendarIcon,
} from '@heroicons/react/24/outline';
import { Plus, MoreVertical, Copy, Trash2, Clock, RefreshCw } from 'lucide-react';
import { useToast } from '../../components/ui/use-toast';

const statusOptions = [
  { value: 'all', label: 'Tümü' },
  { value: 'initiated', label: 'Başlatıldı' },
  { value: 'in_progress', label: 'Devam Ediyor' },
  { value: 'completed', label: 'Tamamlandı' },
  { value: 'cancelled', label: 'İptal Edildi' },
];

const priorityOptions = [
  { value: 'all', label: 'Tümü' },
  { value: 'low', label: 'Düşük' },
  { value: 'medium', label: 'Orta' },
  { value: 'high', label: 'Yüksek' },
  { value: 'critical', label: 'Kritik' },
];

const transportTypeOptions = [
  { value: 'all', label: 'Tümü' },
  { value: 'EVE NAKİL', label: 'Eve Nakil' },
  { value: 'SAĞLIK TEDBİRİ', label: 'Sağlık Tedbiri' },
  { value: 'KONSÜLTASYON', label: 'Konsültasyon' },
  { value: 'SERVİS SEVKİ', label: 'Servis Sevki' },
  { value: 'EVE_NAKIL_EVDE_SAGLIK', label: 'Eve Nakil/Evde Sağlık' },
  { value: 'HASTANEYE_SEVK_EVDE_SAGLIK', label: 'Hastaneye Sevk/Evde Sağlık' },
  { value: 'DİYALİZ SEVK', label: 'Diyaliz Sevk' },
  { value: 'TIBBİ TETKİK', label: 'Tıbbi Tetkik' },
  { value: 'GYB_SEVK', label: 'GYB Sevk' },
  { value: 'NAKIL_RED', label: 'Nakil Red' },
  { value: 'PICU_SEVK', label: 'PICU Sevk' },
  { value: 'BAKIMEVI_SEVK', label: 'BAKIMEVİ Sevk' },
];

const PlannedCasesPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [startDate, setStartDate] = useState(searchParams.get('startDate') || '');
  const [endDate, setEndDate] = useState(searchParams.get('endDate') || '');
  const [showFilters, setShowFilters] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  
  // Auto refresh states
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Dropdown dışında tıklanınca kapat
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.relative')) {
        setActiveDropdown(null);
      }
    };
    
    if (activeDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [activeDropdown]);

  // Auto refresh effect - 3 saniyede bir yenile
  useEffect(() => {
    let intervalId;
    if (autoRefresh) {
      intervalId = setInterval(() => {
        queryClient.invalidateQueries(['plannedCases']);
      }, 3000);
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [autoRefresh, queryClient]);

  // Manuel yenileme fonksiyonu
  const handleManualRefresh = async () => {
    setIsRefreshing(true);
    await queryClient.invalidateQueries(['plannedCases']);
    setTimeout(() => setIsRefreshing(false), 500);
  };

  // Get current filters from URL
  const currentPage = parseInt(searchParams.get('page')) || 1;
  const currentStatus = searchParams.get('status') || 'all';
  const currentBillingInstitution = searchParams.get('billingInstitution') || 'all';
  const currentTransportType = searchParams.get('transportType') || 'all';
  const currentSearch = searchParams.get('search') || '';
  const currentStartDate = searchParams.get('startDate') || '';
  const currentEndDate = searchParams.get('endDate') || '';

  useEffect(() => {
    setStartDate(currentStartDate);
  }, [currentStartDate]);

  useEffect(() => {
    setEndDate(currentEndDate);
  }, [currentEndDate]);

  // Build query parameters - always filter by planned cases
  const buildQueryParams = () => {
    const params = new URLSearchParams();
    params.set('page', currentPage.toString());
    params.set('limit', '200');
    params.set('sort', 'createdAt:desc');
    params.set('is_planned', 'true'); // Only planned cases

    if (currentStatus !== 'all') params.set('status', currentStatus);
    if (currentBillingInstitution !== 'all') params.set('billing_institution', currentBillingInstitution);
    if (currentTransportType !== 'all') params.set('transportType', currentTransportType);
    if (currentSearch) params.set('search', currentSearch);
    if (currentStartDate) params.set('date_from', currentStartDate);
    if (currentEndDate) params.set('date_to', currentEndDate);

    return params.toString();
  };

  // Fetch planned cases
  const { data: casesData, isLoading, error } = useQuery({
    queryKey: ['plannedCases', currentPage, currentStatus, currentBillingInstitution, currentTransportType, currentSearch, currentStartDate, currentEndDate],
    queryFn: () => api.get(`/cases?${buildQueryParams()}`).then(res => res.data),
    staleTime: 30000, // 30 saniye fresh data
    cacheTime: 300000, // 5 dakika cache
  });

  // Fetch institutions for billing filter
  const { data: institutionsData } = useQuery({
    queryKey: ['institutions'],
    queryFn: () => api.get('/institutions').then(res => res.data.data),
  });

  const updateSearchParams = (updates) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value === '' || value === 'all') {
        newParams.delete(key);
      } else {
        newParams.set(key, value);
      }
    });
    // Reset to page 1 when filtering
    if (updates.page === undefined) {
      newParams.set('page', '1');
    }
    setSearchParams(newParams);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    updateSearchParams({ search: searchTerm });
  };

  const handleFilterChange = (filterType, value) => {
    updateSearchParams({ [filterType]: value });
  };

  const handleDateFilterApply = (event) => {
    event.preventDefault();
    updateSearchParams({
      startDate: startDate || '',
      endDate: endDate || ''
    });
  };

  const handleClearDateFilter = () => {
    setStartDate('');
    setEndDate('');
    updateSearchParams({
      startDate: '',
      endDate: ''
    });
  };

  // Vaka kopyalama mutation
  const copyMutation = useMutation({
    mutationFn: async (caseId) => {
      const caseDetail = await api.get(`/cases/${caseId}`).then(res => res.data);
      const orig = caseDetail.data.case;
      const copyData = {
        patient_name: orig.patient_name,
        patient_surname: orig.patient_surname,
        patient_birth_year: orig.patient_birth_year,
        patient_age: orig.patient_age,
        patient_address: orig.patient_address,
        patient_address_details: orig.patient_address_details,
        patient_nationality_type: orig.patient_nationality_type,
        patient_id_number: orig.patient_id_number,
        initial_description: `[KOPYA] ${orig.initial_description || ''}`,
        requesting_authority: orig.requesting_authority,
        call_giver_authority: orig.call_giver_authority,
        case_giver_type: orig.case_giver_type,
        case_giver_person: orig.case_giver_person,
        case_giver_institution: orig.case_giver_institution,
        case_giver_hospital: orig.case_giver_hospital,
        case_giver_presidency_id: orig.case_giver_presidency_id,
        transport_type: orig.transport_type,
        destination_address: orig.destination_address,
        destination_address_details: orig.destination_address_details,
        source_hospital_id: orig.source_hospital_id,
        destination_hospital_id: orig.destination_hospital_id,
        billing_institution: orig.billing_institution,
        protocol_number: '',
        request_date: new Date().toISOString().split('T')[0],
        request_time: new Date().toTimeString().substring(0, 5),
        is_planned: true,
      };
      return api.post('/cases/stage1', copyData);
    },
    onSuccess: () => {
      toast({ title: "Başarılı", description: "Planlı vaka başarıyla kopyalandı", variant: "success" });
      queryClient.invalidateQueries(['plannedCases']);
      setActiveDropdown(null);
    },
    onError: () => {
      toast({ title: "Hata", description: "Planlı vaka kopyalanırken hata oluştu", variant: "destructive" });
      setActiveDropdown(null);
    }
  });

  // Gidiş-dönüş kopyalama mutation
  const copyReturnMutation = useMutation({
    mutationFn: async (caseId) => {
      const caseDetail = await api.get(`/cases/${caseId}`).then(res => res.data);
      const orig = caseDetail.data.case;
      const copyData = {
        patient_name: orig.patient_name,
        patient_surname: orig.patient_surname,
        patient_birth_year: orig.patient_birth_year,
        patient_age: orig.patient_age,
        patient_address: orig.destination_address,
        patient_address_details: orig.destination_address_details,
        patient_nationality_type: orig.patient_nationality_type,
        patient_id_number: orig.patient_id_number,
        initial_description: `[DÖNÜŞ KOPYA] ${orig.initial_description || ''}`,
        requesting_authority: orig.requesting_authority,
        call_giver_authority: orig.call_giver_authority,
        case_giver_type: orig.case_giver_type,
        case_giver_person: orig.case_giver_person,
        case_giver_institution: orig.case_giver_institution,
        case_giver_hospital: orig.case_giver_hospital,
        case_giver_presidency_id: orig.case_giver_presidency_id,
        transport_type: orig.transport_type,
        destination_address: orig.patient_address,
        destination_address_details: orig.patient_address_details,
        source_hospital_id: orig.destination_hospital_id,
        destination_hospital_id: orig.source_hospital_id,
        billing_institution: orig.billing_institution,
        protocol_number: '',
        request_date: new Date().toISOString().split('T')[0],
        request_time: new Date().toTimeString().substring(0, 5),
        is_planned: true,
      };
      return api.post('/cases/stage1', copyData);
    },
    onSuccess: () => {
      toast({ title: "Başarılı", description: "Gidiş-dönüş planlı vaka oluşturuldu", variant: "success" });
      queryClient.invalidateQueries(['plannedCases']);
      setActiveDropdown(null);
    },
    onError: () => {
      toast({ title: "Hata", description: "Gidiş-dönüş vaka oluşturulurken hata oluştu", variant: "destructive" });
      setActiveDropdown(null);
    }
  });

  // Vaka silme mutation
  const deleteMutation = useMutation({
    mutationFn: (caseId) => api.delete(`/cases/${caseId}`),
    onSuccess: () => {
      toast({
        title: "Başarılı",
        description: "Planlı vaka başarıyla silindi",
        variant: "success"
      });
      queryClient.invalidateQueries(['plannedCases']);
      setActiveDropdown(null);
    },
    onError: (error) => {
      toast({
        title: "Hata",
        description: "Planlı vaka silinirken hata oluştu",
        variant: "destructive"
      });
      setActiveDropdown(null);
    }
  });

  const handleCopyCase = (caseItem) => {
    if (window.confirm(`"${caseItem.patient_name} ${caseItem.patient_surname}" planlı vakasını kopyalamak istediğinizden emin misiniz?`)) {
      copyMutation.mutate(caseItem.id);
    }
    setActiveDropdown(null);
  };

  const handleCopyReturnCase = (caseItem) => {
    if (window.confirm(`"${caseItem.patient_name} ${caseItem.patient_surname}" için gidiş-dönüş kopyası oluşturulsun mu? Adresler ve hastaneler ters çevrilecek.`)) {
      copyReturnMutation.mutate(caseItem.id);
    }
    setActiveDropdown(null);
  };

  const handleDeleteCase = (caseItem) => {
    if (window.confirm(`"${caseItem.patient_name} ${caseItem.patient_surname}" planlı vakasını silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.`)) {
      deleteMutation.mutate(caseItem.id);
    }
    setActiveDropdown(null);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'initiated': { label: 'Başlatıldı', color: 'bg-yellow-100 text-yellow-800' },
      'in_progress': { label: 'Devam Ediyor', color: 'bg-orange-100 text-orange-800' },
      'completed': { label: 'Tamamlandı', color: 'bg-green-100 text-green-800' },
      'cancelled': { label: 'İptal Edildi', color: 'bg-red-100 text-red-800' },
    };

    const config = statusConfig[status] || { label: status, color: 'bg-gray-100 text-gray-800' };
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    const priorityConfig = {
      'low': { label: 'Düşük', color: 'bg-blue-100 text-blue-800' },
      'medium': { label: 'Orta', color: 'bg-yellow-100 text-yellow-800' },
      'high': { label: 'Yüksek', color: 'bg-orange-100 text-orange-800' },
      'critical': { label: 'Kritik', color: 'bg-red-100 text-red-800' },
    };

    const config = priorityConfig[priority] || { label: priority, color: 'bg-gray-100 text-gray-800' };
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('tr-TR', {
      timeZone: 'Europe/Istanbul',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatRequestDateTime = (date, time) => {
    if (!date || !time) return 'Belirtilmemiş';
    try {
      // Tarihi normalize et
      let normalizedDate = date;
      
      // Eğer date bir timestamp ise (number veya string timestamp)
      if (typeof date === 'number') {
        normalizedDate = new Date(date).toISOString().split('T')[0];
      } 
      else if (typeof date === 'string' && /^\d{13}$/.test(date)) {
        normalizedDate = new Date(parseInt(date)).toISOString().split('T')[0];
      }
      // ISO string ise
      else if (typeof date === 'string' && date.includes('T')) {
        normalizedDate = date.split('T')[0];
      }
      // YYYY-MM-DD formatında ise
      else if (typeof date === 'string' && date.match(/^\d{4}-\d{2}-\d{2}$/)) {
        normalizedDate = date;
      }
      
      // Saati normalize et  
      let normalizedTime = time;
      if (typeof time === 'string') {
        // HH:MM formatına dönüştür
        if (time.length === 5 && time.includes(':')) {
          normalizedTime = time;
        } else if (time.length === 8 && time.includes(':')) {
          // HH:MM:SS formatından HH:MM'e çevir
          normalizedTime = time.substring(0, 5);
        }
      }
      
      // Tarih ve saati birleştir (YYYY-MM-DD ve HH:MM formatında)
      const dateTimeString = `${normalizedDate}T${normalizedTime}:00`;
      const dateObj = new Date(dateTimeString);
      
      // Geçerli tarih kontrolü
      if (isNaN(dateObj.getTime())) {
        console.error('Invalid date/time:', { date, time, normalized: { normalizedDate, normalizedTime }, combined: dateTimeString });
        return `${normalizedDate} ${normalizedTime}`;
      }
      
      return dateObj.toLocaleString('tr-TR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (error) {
      console.error('Date formatting error:', error, { date, time });
      return `${date} ${time}`;
    }
  };

  const totalPages = Math.ceil((casesData?.data?.pagination?.total_items || 0) / 200);

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Auto Refresh Controls - Sağ üst köşe */}
      <div className="flex justify-end mb-2">
        <div className="flex items-center gap-2 bg-slate-800 rounded-lg px-3 py-1.5 shadow-sm">
          {/* Otomatik Toggle */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="w-4 h-4 rounded border-gray-600 bg-slate-700 text-blue-500 focus:ring-blue-500 focus:ring-offset-slate-800"
            />
            <span className="text-sm text-slate-200 select-none">Otomatik</span>
          </label>
          {/* Manuel Yenile Butonu */}
          <button
            onClick={handleManualRefresh}
            disabled={isRefreshing}
            className="p-1.5 rounded-md hover:bg-slate-700 transition-colors disabled:opacity-50"
            title="Yenile"
          >
            <RefreshCw className={`h-4 w-4 text-slate-300 ${isRefreshing || (autoRefresh && isLoading) ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-8 gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center space-x-2">
            <Clock className="h-8 w-8 text-orange-600" />
            <span>Planlı Vakalar</span>
          </h1>
          <p className="text-muted-foreground">
            İleri tarihli planlı vakaları görüntüleyin ve yönetin
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" onClick={() => navigate('/cases')}>
            Tüm Vakalar
          </Button>
          <Button onClick={() => navigate('/cases/new')}>
            <Plus className="h-4 w-4 mr-2" />
            Yeni Vaka
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex flex-col space-y-4">
            {/* Search Bar */}
            <div className="flex flex-col md:flex-row md:items-center space-y-2 md:space-y-0 md:space-x-4">
              <div className="flex-1">
                <form onSubmit={handleSearch} className="flex space-x-2">
                  <Input
                    placeholder="ASOS protokol, hasta adı, soyadı ile arama..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="flex-1"
                  />
                  <Button type="submit" variant="outline">
                    <MagnifyingGlassIcon className="h-4 w-4" />
                  </Button>
                </form>
              </div>
              <Button
                variant="outline"
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center space-x-2"
              >
                <FunnelIcon className="h-4 w-4" />
                <span>Filtreler</span>
              </Button>
            </div>

            {/* Filters */}
            {showFilters && (
              <>
                <div className="border-t pt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Durum</label>
                    <select
                      value={currentStatus}
                      onChange={(e) => handleFilterChange('status', e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-md text-sm"
                    >
                      {statusOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Faturalanan Kurum</label>
                    <select
                      value={currentBillingInstitution}
                      onChange={(e) => handleFilterChange('billingInstitution', e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-md text-sm"
                    >
                      <option value="all">Tümü</option>
                      {institutionsData?.map((institution) => (
                        <option key={institution.id} value={institution.name}>
                          {institution.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Nakil Türü</label>
                    <select
                      value={currentTransportType}
                      onChange={(e) => handleFilterChange('transportType', e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-md text-sm"
                    >
                      {transportTypeOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="border-t pt-4">
                  <form onSubmit={handleDateFilterApply} className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Başlangıç Tarihi</label>
                      <input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-md text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Bitiş Tarihi</label>
                      <input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-md text-sm"
                      />
                    </div>
                    <div className="flex items-end space-x-2">
                      <Button type="submit" className="flex-1">
                        Tarihleri Uygula
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        disabled={!startDate && !endDate}
                        onClick={handleClearDateFilter}
                      >
                        Temizle
                      </Button>
                    </div>
                  </form>
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <CalendarIcon className="h-5 w-5 text-orange-600" />
            <span>Planlı Vaka Listesi</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <ClockIcon className="h-8 w-8 mx-auto mb-2 text-muted-foreground animate-spin" />
                <p className="text-muted-foreground">Planlı vakalar yükleniyor...</p>
              </div>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <p className="text-red-600">Hata: {error.message}</p>
              <Button variant="outline" onClick={() => window.location.reload()} className="mt-2">
                Yeniden Dene
              </Button>
            </div>
          ) : casesData?.data?.cases?.length === 0 ? (
            <div className="text-center py-8">
              <CalendarIcon className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-medium mb-2">Henüz planlı vaka bulunmuyor</h3>
              <p className="text-muted-foreground mb-4">
                İleri tarihli vaka oluşturmak için "Yeni Vaka" butonuna tıklayın.
              </p>
              <Button onClick={() => navigate('/cases/new')}>
                <Plus className="h-4 w-4 mr-2" />
                Yeni Vaka
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Cases Table */}
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 font-medium">Vaka No</th>
                      <th className="text-left py-3 px-4 font-medium">Hasta</th>
                      <th className="text-left py-3 px-4 font-medium">ASOS Protokol</th>
                      <th className="text-left py-3 px-4 font-medium">Durum</th>
                      <th className="text-left py-3 px-4 font-medium">Planlı Tarih/Saat</th>
                      <th className="text-left py-3 px-4 font-medium">Hasta Adresi</th>
                      <th className="text-left py-3 px-4 font-medium">Gideceği Adres</th>
                      <th className="text-left py-3 px-4 font-medium">Atanan Ekip</th>
                      <th className="text-left py-3 px-4 font-medium">Faturalanan Kurum</th>
                      <th className="text-left py-3 px-4 font-medium">İşlemler</th>
                    </tr>
                  </thead>
                  <tbody>
                    {casesData?.data?.cases?.map((caseItem) => (
                      <tr key={caseItem.id} className={`border-b ${caseItem.case_giver_type === 'presidency' ? 'border-teal-400 bg-gradient-to-r from-orange-50 to-teal-50 hover:from-orange-100 hover:to-teal-100' : 'border-orange-300 bg-orange-50 hover:bg-orange-100'}`}>
                        <td className="py-3 px-4">
                          <Link 
                            to={`/cases/${caseItem.id}`}
                            className="font-mono text-sm text-blue-600 hover:text-blue-800"
                          >
                            #{caseItem.case_number}
                            <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-orange-200 text-orange-800">
                              Planlı
                            </span>
                            {caseItem.case_giver_type === 'presidency' && (
                              <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: '#b2f0ec', color: '#0d7a73' }}>
                                112 Vakası
                              </span>
                            )}
                          </Link>
                        </td>
                        <td className="py-3 px-4">
                          <div>
                            <div className="font-medium">
                              {caseItem.patient_name} {caseItem.patient_surname}
                            </div>
                            {caseItem.patient_age && (
                              <div className="text-sm text-muted-foreground">
                                {caseItem.patient_age} yaş
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="text-sm">
                            {caseItem.protocol_number || '-'}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          {getStatusBadge(caseItem.status)}
                        </td>
                        <td className="py-3 px-4">
                          <div className="text-sm">
                            <div className="font-medium text-orange-700">
                              {new Date(caseItem.request_date).toLocaleDateString('tr-TR', {
                                timeZone: 'Europe/Istanbul'
                              })} {caseItem.request_time}
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="text-sm font-medium">
                            {caseItem.patient_address || '-'}
                          </div>
                          {caseItem.patient_address_details && (
                            <div className="text-xs text-muted-foreground mt-1">
                              {caseItem.patient_address_details}
                            </div>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <div className="text-sm font-medium">
                            {caseItem.destination_address || '-'}
                          </div>
                          {caseItem.destination_address_details && (
                            <div className="text-xs text-muted-foreground mt-1">
                              {caseItem.destination_address_details}
                            </div>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <div className="text-sm">
                            {caseItem.team_name || 'Atanmamış'}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="text-sm">
                            {caseItem.billing_institution || 'Atanmamış'}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center space-x-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => navigate(`/cases/${caseItem.id}`)}
                            >
                              <EyeIcon className="h-4 w-4 mr-1" />
                              Detay Gör
                            </Button>
                            
                            {/* Üç nokta menüsü */}
                            <div className="relative">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setActiveDropdown(activeDropdown === caseItem.id ? null : caseItem.id)}
                                className="p-2"
                              >
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                              
                              {activeDropdown === caseItem.id && (
                                <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 min-w-[120px]">
                                  <button
                                    onClick={() => handleCopyCase(caseItem)}
                                    disabled={copyMutation.isLoading}
                                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center space-x-2 first:rounded-t-lg"
                                  >
                                    <Copy className="h-4 w-4" />
                                    <span>Kopyala</span>
                                  </button>
                                  <button
                                    onClick={() => handleCopyReturnCase(caseItem)}
                                    disabled={copyReturnMutation.isLoading}
                                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center space-x-2"
                                  >
                                    <Copy className="h-4 w-4" />
                                    <span>Dönüş Kopyala</span>
                                  </button>
                                  <button
                                    onClick={() => handleDeleteCase(caseItem)}
                                    disabled={deleteMutation.isLoading}
                                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 text-red-600 flex items-center space-x-2 last:rounded-b-lg"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                    <span>Sil</span>
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {casesData?.data?.pagination && (
                <div className="flex items-center justify-between">
                  <div className="text-sm text-muted-foreground">
                    {casesData?.data?.pagination?.total_items || 0} planlı vakanın {' '}
                    {((currentPage - 1) * 200) + 1}-{Math.min(currentPage * 200, casesData?.data?.pagination?.total_items || 0)} arası gösteriliyor
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={currentPage <= 1}
                      onClick={() => updateSearchParams({ page: currentPage - 1 })}
                    >
                      Önceki
                    </Button>
                    <div className="flex items-center space-x-1">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        const page = i + 1;
                        return (
                          <Button
                            key={page}
                            variant={currentPage === page ? "default" : "outline"}
                            size="sm"
                            onClick={() => updateSearchParams({ page })}
                          >
                            {page}
                          </Button>
                        );
                      })}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={currentPage >= totalPages}
                      onClick={() => updateSearchParams({ page: currentPage + 1 })}
                    >
                      Sonraki
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PlannedCasesPage; 